// Internal-only access gate. Rendered around the whole app when
// VITE_INTERNAL_ONLY === '1' (dev/stage). Restricts the SPA to seeded internal
// users. OTP-only (no password): enter your email, then a one-time code emailed
// to you. Once a device is verified it is trusted, so future logins on that
// browser/machine skip the OTP step.
//
// Unrecognized email → a "Request access" form (name + phone) that emails the
// platform admin. The admin signs in and, at ?internal=requests, reviews the
// queued requests and Approves/Rejects them. Self-contained (no app contexts).
import { useCallback, useEffect, useState } from 'react'

// Deployed builds call VITE_API_URL/api; local dev uses relative URLs.
// Domain origin only (strip any path VITE_API_URL may include, e.g. /api/v1),
// then append the app's actual route prefix below. This keeps the gate correct
// whether VITE_API_URL is "https://dev-api.x.com" or "https://dev-api.x.com/api".
let API_ORIGIN = ''
try {
  if (import.meta.env.PROD && import.meta.env.VITE_API_URL) {
    API_ORIGIN = new URL(import.meta.env.VITE_API_URL).origin
  }
} catch { API_ORIGIN = '' }
// ⚠ Specze registers routes/api.php the DEFAULT Laravel way, so every route
//   carries the /api prefix — confirmed against the live route list
//   (api/internal-access/session). Some sibling apps strip it because their
//   domain is already api.*; this one does not. A wrong value here 404s every
//   gate call, which the gate then reads as 'no gate' and stands down — i.e.
//   it fails OPEN. Worth checking against Could not open input file: artisan per app.
const API_PREFIX = '/api'
const API_BASE = `${API_ORIGIN}${API_PREFIX}`

const TOKEN_KEY = 'internal_access_token'
const DEVICE_ID_KEY = 'internal_access_device_id'

// Admin review page is requested via ?internal=requests (link in the email).
const REVIEW_MODE = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('internal') === 'requests'

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) { id = uuid(); localStorage.setItem(DEVICE_ID_KEY, id) }
  return id
}

async function sha256Hex(str) {
  const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

function fingerprintSource() {
  const n = window.navigator
  return [
    n.userAgent, n.language, n.platform,
    `${window.screen?.width}x${window.screen?.height}x${window.screen?.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ].join('|')
}

function deviceLabel() {
  const ua = window.navigator.userAgent
  const browser = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome'
    : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Browser'
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux'
    : /iPhone|iPad/.test(ua) ? 'iOS' : 'Unknown OS'
  return `${browser} on ${os}`
}

// ⚠ A THROWN FETCH MUST NOT REACH THE CALLER. The session probe now runs on
//   EVERY load rather than only when a token exists, so an offline visitor, a
//   VPN or a dead API would reject this promise inside the mount effect, no
//   phase would ever be set, and the gate would sit on "Checking your session…"
//   for ever. Before the probe moved, a visitor with no token never called the
//   API at all and could not hit this. Returning a sentinel instead lets the
//   caller fall through to the email form exactly as it used to.
async function call(path, { method = 'POST', body, token } = {}) {
  let res
  try {
    res = await fetch(`${API_BASE}/internal-access/${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  } catch {
    return { ok: false, status: 0, data: {}, networkError: true }
  }
  let data = {}
  try { data = await res.json() } catch { /* an HTML 500 body — ignore */ }
  return { ok: res.ok, status: res.status, data }
}

const post = (path, body, token) => call(path, { method: 'POST', body, token })

// Self-disabling: outside dev/stage (flag != '1') this is a passthrough, so it
// can be wired into every app's entry unconditionally.
//
// The gate is split in two so the flag check happens BEFORE any hook is called.
// Previously the check was an early return at the top of the hook-bearing
// component, which meant 17 hooks sat after a conditional return — safe only
// because the flag is inlined at build time, and silently broken the moment it
// became runtime-conditional. Now the branch picks a component instead.
// Specze's palette — the violet scale tailwind.config.js defines plus the
// near-black grounds the app is built on, repeated as literals because this
// component is deliberately self-contained (see the porting note below).
//
// ⚠ MEASURED, NOT ASSUMED. Contrast against the card surface #0d0521:
//     white ............ 19.80  AA
//     violet-200 ....... 12.44  AA
//     violet-300 ........ 8.52  AA
//     blue #60a5fa ...... 7.79  AA
//     violet-400 ........ 5.60  AA
//     muted ............. 5.45  AA
//     violet-500 ........ 4.01  AA-large only  ← the brand shade
//     violet-600 ........ 3.30  AA-large only
//
// ⚠⚠ THE BRAND VIOLET IS NOT A TEXT COLOUR. violet-500 is the identity tone
//   and measures 4.01 on the card; violet-600 is worse at 3.30. Both are fine
//   as FILLS — white on violet-500 is 4.94 and on violet-600 is 6.01 — so the
//   button is a 600→500 gradient with a white label, while every piece of
//   violet TYPE uses violet-300 or violet-400. It is the fill that works here
//   and the type that does not, which is the opposite of most of these.
const BG       = '#07020f'
const SURFACE  = '#0d0521'
const ELEVATED = '#180a3a'
const TEXT     = '#ffffff'
const MUTED    = '#8b8396'
const V200     = '#d6c4ff'
const V300     = '#b899ff' // safe as type
const V400     = '#9d6aff' // safe as type
const V500     = '#8b3fff' // FILLS ONLY
const V600     = '#7c1fff' // FILLS ONLY
const BLUE     = '#60a5fa'
const ERROR    = '#f87171'

const DISPLAY = "Syne, ui-sans-serif, system-ui, sans-serif"
const UI = "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif"
const MONO = "'JetBrains Mono', ui-monospace, monospace"

const BLEED      = 40                        // how far the drawing grows past each edge
// ⚠ On a dark ground the drawing's inks are tints of the TEXT. The app's own
//   border tones are ~1.3:1 here — invisible before a blur and a scrim are
//   applied on top. A sibling product's first attempt was built from exactly
//   that kind of token and rendered a ghost.
const GHOST      = 'rgba(255,255,255,0.17)'
const GHOST_SOFT = 'rgba(255,255,255,0.07)'
const EDGE       = 'rgba(255,255,255,0.14)'

// ── The app shell behind the dialog ───────────────────────────────────────
// Someone who lands on a closed door should still be able to see what is
// behind it. A flat void gives a visitor nothing to judge, and reads more like
// a parked domain than a product that is not open yet.
//
// ⚠⚠⚠ THIS IS A DRAWING, NOT THE APP, AND IT MUST STAY THAT WAY.
//   The obvious implementation — render `children` blurred underneath — is a
//   hole straight through the gate: the app would MOUNT and run every fetch it
//   makes on load for a visitor who has not been authorized, and `filter:
//   blur()` is decoration that any inspector removes. This product's payload is
//   other people's SOURCE CODE and the specs derived from it. So this renders
//   fixed markup and nothing else: no router, no contexts, no fetches,
//   `aria-hidden` and `pointer-events: none`.
//
// ⚠⚠ AND NO REPOSITORY IS INVENTED. No org, no repo name, no branch, no commit
//   hash, no line of code — not even plausible-looking filler. A tool that
//   reads private repositories must not put something that looks like one on
//   its login screen. The file tree uses only UNIVERSAL path names that belong
//   to no project in particular (src/, lib/, tests/, README.md), and the
//   generated panel beside it is heading and paragraph bars with a checklist.
//   No spec text, no test names, no counts.
function AppPreview() {
  const nav = ['Product', 'Docs', 'Pricing', 'Changelog']
  return (
    <div style={P.root} aria-hidden="true">
      {/* Inline styles cannot express a media query, and at phone width the
          pipeline squeezes the hero column to nothing. It goes; the cards stay,
          stacked, so the lower half is not an empty slab. */}
      <style>{`
        @media (max-width: 880px) {
          .sz-ig-pipe { display: none !important; }
          .sz-ig-cards { flex-direction: column !important; }
          .sz-ig-hero { padding-top: 34px !important; }
          .sz-ig-title { font-size: 32px !important; }
        }
      `}</style>

      <div style={P.nav}>
        <div style={P.navInner}>
          <span style={P.wordmark}>Specze</span>
          <div style={P.navLinks}>
            {nav.map((l) => <span key={l} style={P.navLink}>{l}</span>)}
          </div>
          <span style={P.navBtn}>Start free</span>
        </div>
      </div>

      <div style={P.hero} className="sz-ig-hero">
        <div style={P.heroCopy}>
          <span style={P.eyebrow}>Repo to spec, automatically</span>
          <h2 style={P.heroTitle} className="sz-ig-title">
            Git repositories into<br />
            <em style={P.heroEm}>specs, docs &amp; test plans.</em>
          </h2>
          <div style={{ ...P.bar, width: '90%' }} />
          <div style={{ ...P.bar, width: '64%' }} />
          <div style={P.heroBtns}>
            <span style={P.btnSolid} />
            <span style={P.btnGhost} />
          </div>
        </div>
        <Pipeline />
      </div>

      <div style={P.cardRow} className="sz-ig-cards">
        {[V500, BLUE, V300].map((c) => (
          <div key={c} style={P.card}>
            <div style={{ ...P.cardIcon, background: `${c}2E`, border: `1px solid ${c}59` }} />
            <div style={{ ...P.bar, width: '72%', height: 11 }} />
            <div style={{ ...P.bar, width: '48%', height: 11, margin: 0 }} />
          </div>
        ))}
      </div>
    </div>
  )
}

// The pipeline — a source tree on the left, the generated document on the
// right. That transformation IS the product. Fixed, not random: a backdrop
// that reshuffles between renders is a distraction.
function Pipeline() {
  // Universal path names only. These belong to no repository in particular,
  // which is the point — see the note above.
  const TREE = [
    { n: 'src/', d: 0, dir: true },
    { n: 'api/', d: 1, dir: true },
    { n: 'lib/', d: 1, dir: true },
    { n: 'tests/', d: 0, dir: true },
    { n: 'README.md', d: 0, dir: false },
  ]
  return (
    <div style={P.pipe} className="sz-ig-pipe">
      <div style={P.pipeChrome}>
        {[ERROR, V400, BLUE].map((c) => (
          <span key={c} style={{ ...P.dot, background: c }} />
        ))}
      </div>
      <div style={P.pipeBody}>
        <div style={P.tree}>
          {TREE.map((t) => (
            <div key={t.n} style={{ ...P.treeRow, paddingLeft: 6 + t.d * 12 }}>
              <span style={{ ...P.treeMark, background: t.dir ? `${V400}59` : `${BLUE}59` }} />
              <span style={P.treeName}>{t.n}</span>
            </div>
          ))}
          <div style={P.arrow}>
            <span style={P.arrowLine} />
          </div>
        </div>

        <div style={P.doc}>
          {/* The generated document: a title, body, then a test-plan checklist */}
          <div style={{ ...P.bar, height: 9, width: '62%', margin: '0 0 10px' }} />
          {['92%', '78%', '86%'].map((w, i) => (
            <div key={i} style={{ ...P.bar, height: 5, width: w, margin: '0 0 6px' }} />
          ))}
          <div style={P.checks}>
            {[1, 1, 0, 1].map((on, i) => (
              <div key={i} style={P.checkRow2}>
                <span style={{
                  ...P.checkBox,
                  background: on ? V500 : 'transparent',
                  border: `1.5px solid ${on ? V500 : EDGE}`,
                }} />
                <span style={{ ...P.bar, height: 5, width: i % 2 ? '58%' : '72%', margin: 0 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function InternalGate({ children }) {
  if (import.meta.env.VITE_INTERNAL_ONLY !== '1') return children
  return <InternalGateImpl>{children}</InternalGateImpl>
}

function InternalGateImpl({ children }) {
  const [phase, setPhase] = useState('checking') // checking | email | otp | request | requested | authed | disabled
  const [deviceHash, setDeviceHash] = useState(null)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [phone, setPhone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [sameWa, setSameWa] = useState(true)
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [stateCode, setStateCode] = useState('')
  const [stateText, setStateText] = useState('')
  const [consent, setConsent] = useState(false)
  const [countries, setCountries] = useState([])
  const [subdivisions, setSubdivisions] = useState({})
  const [geoError, setGeoError] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    let alive = true
    ;(async () => {
      const dh = await sha256Hex(`${getDeviceId()}|${fingerprintSource()}`)
      if (!alive) return
      setDeviceHash(dh)
      const token = localStorage.getItem(TOKEN_KEY)

      // ⚠⚠⚠ ASK THE BACKEND WHETHER THE GATE IS ON, AND STAND DOWN IF IT IS NOT.
      //   InternalAccessController::ensureEnabled() returns 404 on every gate
      //   endpoint when INTERNAL_ONLY is off. Before this, with no token the
      //   gate jumped straight to the email form without asking the backend
      //   anything, then 404ed on `geo` and again on `request-otp` and printed
      //   the raw envelope "The requested resource was not found." under the
      //   email field — a production site nobody could get past, including the
      //   admin, reporting a missing route rather than a disabled feature.
      //   This exact failure took the sibling product's production site down.
      //
      //   A 404 from a gate endpoint is not an error. It is the backend saying
      //   "I do not have this gate", and the only correct response is to let
      //   the app through. The probe is safe without a token because
      //   ensureEnabled() is the first line of session(), ahead of validation
      //   and the bearer check.
      const probe = await post('session', { device_hash: dh }, token || undefined)
      if (!alive) return
      if (probe.status === 404) { setPhase('disabled'); return }

      if (!token) { setPhase('email'); return }
      const { ok, data } = probe
      if (ok && data.valid) { setIsAdmin(!!data.user?.is_admin); setPhase('authed') }
      else { localStorage.removeItem(TOKEN_KEY); setPhase('email') }
    })()
    return () => { alive = false }
  }, [])

  const requestCode = useCallback(async () => {
    return post('request-otp', {
      email: email.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    })
  }, [email, deviceHash])

  const submitEmail = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError(''); setNotice('')
    const { ok, status, data } = await requestCode()
    setBusy(false)
    if (!ok) {
      // Same reasoning as the mount probe: 404 here is "no gate", not an error.
      if (status === 404) { setPhase('disabled'); return }
      if (data.can_request) { setPhase('request'); return }
      setError(data.message || 'Could not sign in.'); return
    }
    if (data.status === 'authenticated') {
      localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed'); return
    }
    setNotice(`We emailed a 6-digit code to ${data.email || email}.`)
    setPhase('otp')
  }, [requestCode, email])

  const submitOtp = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    const { ok, data } = await post('verify-otp', {
      email: email.trim(), code: code.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    })
    setBusy(false)
    if (!ok) { setError(data.message || 'Invalid code.'); return }
    localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed')
  }, [email, code, deviceHash])

  const resend = useCallback(async () => {
    setBusy(true); setError(''); setNotice('')
    const { ok, data } = await requestCode()
    setBusy(false)
    if (!ok) { setError(data.message || 'Could not resend code.'); return }
    if (data.status === 'authenticated') {
      localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed'); return
    }
    setNotice('A new code is on its way.')
  }, [requestCode])

  // Load the country/subdivision lists as soon as the wall is interactive (the
  // email step), not only when the request form shows — so the dropdown is
  // already populated by the time an unrecognised user reaches it. Retries a
  // couple of times and surfaces a real error instead of swallowing it, because
  // a silently-empty <select> looks like a broken form.
  useEffect(() => {
    if (phase === 'checking' || phase === 'authed' || phase === 'disabled' || countries.length) return
    let cancelled = false
    const load = async (attempt = 0) => {
      try {
        const r = await fetch(`${API_BASE}/internal-access/geo`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        })
        if (!r.ok) throw new Error(`geo HTTP ${r.status}`)
        const d = await r.json()
        if (cancelled) return
        if (!Array.isArray(d.countries) || !d.countries.length) throw new Error('geo returned no countries')
        setCountries(d.countries)
        setSubdivisions(d.subdivisions || {})
        setGeoError(false)
      } catch (err) {
        if (cancelled) return
        if (attempt < 2) { window.setTimeout(() => { void load(attempt + 1) }, 700 * (attempt + 1)); return }
        console.warn('[internal-gate] failed to load country list:', err)
        setGeoError(true)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [phase, countries.length])

  const submitRequest = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError('')

    const tidy = (v) => v.replace(/[\s()-]/g, '')
    const hasList = (subdivisions[country] || []).length > 0

    const { ok, data } = await post('request-access', {
      email: email.trim(),
      first_name: first.trim(),
      last_name: last.trim(),
      phone: tidy(phone.trim()),
      whatsapp: tidy((sameWa ? phone : whatsapp).trim()),
      city: city.trim(),
      country_code: country,
      state_code: hasList ? stateCode : null,
      state: hasList ? null : stateText.trim(),
      consent,
    })
    setBusy(false)
    if (!ok) {
      // ⚠️ This API reshapes 422s: there is no Laravel `errors` bag, only
      // details:[{field,message}]. Reading `errors` here would silently fall
      // back to the generic message and hide "include your country code".
      const first422 = Array.isArray(data?.details) ? data.details[0]?.message : null
      setError(first422 || data.message || 'Could not submit your request.'); return
    }
    if (data.status === 'already_authorized') {
      setNotice('You already have access — sign in with your email.'); setPhase('email'); return
    }
    setPhase('requested')
  }, [email, first, last, phone, whatsapp, sameWa, city, country, stateCode, stateText, consent, subdivisions])

  // The backend has the gate switched off. Render the app.
  if (phase === 'disabled') return children

  if (phase === 'authed') {
    if (REVIEW_MODE && isAdmin) return <ReviewPage token={localStorage.getItem(TOKEN_KEY)} />
    return children
  }

  return (
    <div style={S.wrap}>
      <AppPreview />
      {/* A wash over the drawing: enough to hold the dialog contrast
          without flattening the shell back into the void it replaced. */}
      <div style={S.scrim} aria-hidden="true" />
      {/* The request form carries far more fields than the sign-in steps, so it
          gets a wider card rather than a long narrow column. */}
      <div style={{ ...S.card, maxWidth: phase === 'request' ? 460 : 400 }}>
        <div style={S.brandRow}>
          <span style={S.badge}>{import.meta.env.VITE_APP_NAME || 'Specze'}</span>
          <span style={S.internalTag}>Authorized access</span>
        </div>

        {phase === 'checking' && <div style={S.checking}>Checking your session…</div>}

        {phase === 'email' && (
          <form onSubmit={submitEmail}>
            <h1 style={S.title}>Sign in to continue</h1>
            <p style={S.sub}>Access is limited to authorized users. Enter your email and we’ll send you a one-time code.</p>
            <label style={S.label}>Email</label>
            <input style={S.input} type="email" autoFocus required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Please wait…' : 'Send code'}
            </button>
          </form>
        )}

        {phase === 'otp' && (
          <form onSubmit={submitOtp}>
            <h1 style={S.title}>Enter your code</h1>
            {notice && <p style={S.sub}>{notice}</p>}
            <label style={S.label}>6-digit code</label>
            <input style={{ ...S.input, letterSpacing: '0.4em', textAlign: 'center', fontSize: 20 }}
              inputMode="numeric" autoFocus required value={code} maxLength={6}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••" />
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
            <div style={S.row}>
              <button type="button" style={S.linkBtn} onClick={resend} disabled={busy}>Resend code</button>
              <button type="button" style={S.linkBtn} onClick={() => { setPhase('email'); setCode(''); setError('') }}>
                Use a different email
              </button>
            </div>
          </form>
        )}

        {phase === 'request' && (
          <form onSubmit={submitRequest}>
            <h1 style={S.title}>Request access</h1>
            <p style={S.sub}>
              Your email isn’t on the authorized list yet. Tell us how to reach you and the platform admin
              will verify by phone or WhatsApp before letting you in.
            </p>
            <label style={S.label}>Email address</label>
            <input style={{ ...S.input, opacity: 0.65, cursor: 'not-allowed' }} type="email" value={email} disabled readOnly />
            <div style={S.grid2}>
              <div>
                <label style={S.label}>First name</label>
                <input style={S.input} autoFocus required value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First" />
              </div>
              <div>
                <label style={S.label}>Last name</label>
                <input style={S.input} required value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last" />
              </div>
            </div>
            <label style={S.label}>Phone number</label>
            <input
              style={S.input} type="tel" required value={phone}
              onChange={(e) => { setPhone(e.target.value); if (sameWa) setWhatsapp(e.target.value) }}
              placeholder="+91 98765 43210"
            />
            <div style={S.hint}>Include your country code, starting with +.</div>

            <label style={S.checkRow}>
              <input
                type="checkbox" checked={sameWa}
                onChange={(e) => { setSameWa(e.target.checked); if (e.target.checked) setWhatsapp(phone) }}
              />
              <span>My WhatsApp number is the same</span>
            </label>

            {!sameWa && (
              <>
                <label style={S.label}>WhatsApp number</label>
                <input style={S.input} type="tel" required value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)} placeholder="+91 98765 43210" />
              </>
            )}

            <label style={S.label}>Country</label>
            <select
              style={S.input} required value={country}
              onChange={(e) => { setCountry(e.target.value); setStateCode(''); setStateText('') }}
            >
              <option style={S.option} value="">{countries.length ? 'Select a country…' : (geoError ? 'Could not load countries' : 'Loading countries…')}</option>
              {countries.map((c) => <option style={S.option} key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            {geoError && (
              <button type="button" style={{ ...S.linkBtn, marginTop: 6 }}
                onClick={() => { setGeoError(false); setCountries([]) }}>
                Retry loading the country list
              </button>
            )}

            <label style={S.label}>State / Province</label>
            {subdivisions[country]?.length ? (
              <select style={S.input} required value={stateCode} onChange={(e) => setStateCode(e.target.value)}>
                <option style={S.option} value="">Select a state…</option>
                {subdivisions[country].map((st) => <option style={S.option} key={st.code} value={st.code}>{st.name}</option>)}
              </select>
            ) : (
              <input style={S.input} required value={stateText} onChange={(e) => setStateText(e.target.value)}
                placeholder={country ? 'State or province' : 'Select a country first'} disabled={!country} />
            )}

            <label style={S.label}>City</label>
            <input style={S.input} required value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />

            <label style={S.checkRow}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required />
              <span>
                I agree to be contacted on these numbers to verify this request. My details are used only
                to review it and are not shared.
              </span>
            </label>

            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Sending…' : 'Request access'}
            </button>
            <div style={S.row}>
              <button type="button" style={S.linkBtn} onClick={() => { setPhase('email'); setError('') }}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {phase === 'requested' && (
          <div>
            <h1 style={S.title}>Request sent</h1>
            <p style={S.sub}>Thanks — we’ve emailed the platform admin. You’ll be able to sign in with a one-time code once your request is approved.</p>
            <button style={S.btn} type="button" onClick={() => { setPhase('email'); setFirst(''); setLast(''); setPhone(''); setError('') }}>
              Done
            </button>
          </div>
        )}

        <div style={S.foot}>Trusted devices skip the code next time · A Stacza product</div>
      </div>
    </div>
  )
}

// ── Admin review page (?internal=requests) ─────────────────────────────────
function ReviewPage({ token }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState('')
  const [actingId, setActingId] = useState(null)

  const load = useCallback(async () => {
    const { ok, data } = await call('requests', { method: 'GET', token })
    if (!ok) { setError(data.message || 'Could not load requests.'); setRows([]); return }
    setRows(data.data || [])
  }, [token])

  useEffect(() => { load() }, [load])

  const act = useCallback(async (id, action) => {
    setActingId(id); setError('')
    const { ok, data } = await post(`requests/${id}/${action}`, {}, token)
    setActingId(null)
    if (!ok) { setError(data.message || `Could not ${action}.`); return }
    load()
  }, [token, load])

  const fmtDate = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
  }

  return (
    <div style={{ ...S.wrap, alignItems: 'flex-start', padding: '40px 20px' }}>
      <div style={{ ...S.card, maxWidth: 860 }}>
        <div style={S.brandRow}>
          <span style={S.badge}>{import.meta.env.VITE_APP_NAME || 'Specze'}</span>
          <span style={S.internalTag}>Access requests</span>
        </div>
        <h1 style={S.title}>Access requests</h1>
        <p style={S.sub}>Newest first. Verify the person by phone or WhatsApp before approving. Approval grants access on this environment only and is held in the database, never written into the codebase.</p>
        {error && <div style={S.error}>{error}</div>}

        {rows === null && <div style={S.checking}>Loading…</div>}
        {rows !== null && rows.length === 0 && <div style={S.checking}>No access requests yet.</div>}

        {rows !== null && rows.length > 0 && (
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Requester</th>
                  <th style={S.th}>Phone</th>
                  <th style={S.th}>Requested</th>
                  <th style={S.th}>Status</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{`${r.first_name} ${r.last_name}`.trim() || '—'}</div>
                      <div style={S.tdSub}>{r.email}</div>
                    </td>
                    <td style={S.td}>{r.phone || '—'}</td>
                    <td style={S.td}>{fmtDate(r.created_at)}</td>
                    <td style={S.td}><StatusBadge status={r.status} /></td>
                    <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.status === 'pending' ? (
                        <>
                          <button style={S.approveBtn} disabled={actingId === r.id} onClick={() => act(r.id, 'approve')}>Approve</button>
                          <button style={S.rejectBtn} disabled={actingId === r.id} onClick={() => act(r.id, 'reject')}>Reject</button>
                        </>
                      ) : (
                        <span style={S.tdSub}>
                          {r.reviewed_by ? `by ${r.reviewed_by}` : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={S.foot}>
          <a href={window.location.pathname} style={{ color: MUTED }}>← Back to app</a>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status }) {
  const map = {
    pending:  { bg: `${V500}1F`, bd: `${V500}4D`, c: V300,  t: 'Pending' },
    approved: { bg: `${BLUE}1A`, bd: `${BLUE}4D`, c: BLUE,  t: 'Approved' },
    rejected: { bg: `${ERROR}1A`, bd: `${ERROR}4D`, c: ERROR, t: 'Rejected' },
  }
  const s = map[status] || map.pending
  return (
    <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
      background: s.bg, border: `1px solid ${s.bd}`, color: s.c }}>{s.t}</span>
  )
}

// ── The drawing behind the dialog ─────────────────────────────────────────
// `pointer-events: none` on the root is load-bearing, not cosmetic: it is what
// stops a visitor interacting with anything here, including selecting text.
const P = {
  // ⚠ A BLEED MARGIN, NOT A SCALE. `transform: scale()` also pushes the blur's
  //   soft edge off-screen, but it does it by enlarging about the centre, which
  //   crops the top of the viewport and slices the navbar in half.
  root: {
    position: 'fixed', inset: -BLEED, overflow: 'hidden', pointerEvents: 'none',
    userSelect: 'none', fontFamily: UI, padding: `${BLEED}px ${BLEED}px 0`,
    boxSizing: 'border-box', filter: 'blur(3.5px) saturate(1.05)',
    background: `radial-gradient(110% 80% at 80% 4%, ${V500}2E 0%, ${V500}00 55%),
                 radial-gradient(90% 70% at 8% 92%, ${BLUE}14 0%, ${BLUE}00 60%),
                 linear-gradient(180deg, ${BG} 0%, ${SURFACE} 70%, ${BG} 100%)`,
  },
  nav: { background: `${BG}D9`, borderBottom: `1px solid ${EDGE}` },
  navInner: { maxWidth: 1180, margin: '0 auto', padding: '0 28px', height: 68, display: 'flex', alignItems: 'center', gap: 26 },
  wordmark: { fontFamily: DISPLAY, fontWeight: 800, fontSize: 22, color: TEXT, letterSpacing: '-0.02em' },
  navLinks: { display: 'flex', gap: 20, flex: 1, flexWrap: 'nowrap', overflow: 'hidden' },
  navLink: { fontSize: 13.5, color: MUTED, whiteSpace: 'nowrap' },
  // White on violet-600 is 6.01 — the fill works even though violet TYPE does not.
  navBtn: { fontSize: 13, fontWeight: 700, color: '#FFFFFF', whiteSpace: 'nowrap', background: `linear-gradient(120deg, ${V600}, ${V500})`, padding: '10px 18px', borderRadius: 9 },

  hero: { maxWidth: 1180, margin: '0 auto', padding: '54px 28px 36px', display: 'flex', alignItems: 'center', gap: 44 },
  heroCopy: { flex: 1, minWidth: 0 },
  // ⚠ violet-300 for the label, never violet-500 (4.01).
  eyebrow: { display: 'inline-block', fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: V300, background: `${V500}1F`, border: `1px solid ${V500}4D`, padding: '5px 12px', borderRadius: 999, marginBottom: 18 },
  heroTitle: { fontFamily: DISPLAY, fontSize: 44, lineHeight: 1.1, color: TEXT, margin: '0 0 22px', fontWeight: 800, letterSpacing: '-0.03em' },
  heroEm: { fontStyle: 'normal', backgroundImage: `linear-gradient(90deg, ${V300}, ${BLUE})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' },
  bar: { height: 12, borderRadius: 999, background: GHOST, margin: '0 0 10px' },
  heroBtns: { display: 'flex', gap: 12, marginTop: 26 },
  btnSolid: { width: 150, height: 46, borderRadius: 9, background: `linear-gradient(120deg, ${V600}, ${V500})` },
  btnGhost: { width: 130, height: 46, borderRadius: 9, border: `1.5px solid ${MUTED}` },

  // The repo → document pipeline
  pipe: { flexShrink: 0, width: 338, background: SURFACE, borderRadius: 13, border: `1px solid ${EDGE}`, overflow: 'hidden', boxShadow: '0 24px 54px rgba(0,0,0,0.6)' },
  pipeChrome: { height: 30, background: ELEVATED, borderBottom: `1px solid ${EDGE}`, display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px' },
  dot: { width: 9, height: 9, borderRadius: 999, display: 'inline-block' },
  pipeBody: { display: 'flex', minHeight: 232 },
  tree: { width: 132, background: ELEVATED, borderRight: `1px solid ${EDGE}`, padding: '12px 8px', position: 'relative' },
  treeRow: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 },
  treeMark: { width: 11, height: 11, borderRadius: 3, flexShrink: 0 },
  treeName: { fontFamily: MONO, fontSize: 10.5, color: V200, whiteSpace: 'nowrap' },
  arrow: { position: 'absolute', right: -1, top: '50%', width: 18, height: 2 },
  arrowLine: { display: 'block', width: '100%', height: '100%', background: `linear-gradient(90deg, ${V500}, ${V500}00)` },
  doc: { flex: 1, padding: 14, minWidth: 0 },
  checks: { marginTop: 12, paddingTop: 10, borderTop: `1px solid ${EDGE}` },
  checkRow2: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  checkBox: { width: 12, height: 12, borderRadius: 3, flexShrink: 0 },

  cardRow: { maxWidth: 1180, margin: '0 auto', padding: '0 28px', display: 'flex', gap: 20 },
  card: { flex: 1, background: SURFACE, border: `1px solid ${EDGE}`, borderRadius: 13, padding: 18, boxShadow: '0 14px 32px rgba(0,0,0,0.4)' },
  cardIcon: { width: 38, height: 38, borderRadius: 9, marginBottom: 16 },
}

const S = {
  wrap: { position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: BG, fontFamily: UI, color: TEXT },
  // ⚠ A WASH, not a lid: a sibling product's first version sat at 94% opacity
  // in the centre and erased the shell it was meant to reveal.
  scrim: { position: 'fixed', inset: 0, pointerEvents: 'none', background: `radial-gradient(ellipse at center, ${BG}C7 0%, ${BG}99 45%, ${BG}54 100%)` },
  card: { position: 'relative', zIndex: 1, width: '100%', maxWidth: 400, background: SURFACE, border: `1px solid ${EDGE}`, borderRadius: 16, padding: '32px 28px', boxShadow: '0 40px 90px rgba(0,0,0,0.75), 0 2px 10px rgba(0,0,0,0.5)' },
  brandRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26 },
  badge: { fontFamily: DISPLAY, fontWeight: 800, fontSize: 20, color: TEXT, letterSpacing: '-0.02em' },
  // ⚠ violet-300, not the brand violet-500 (4.01). This chip is the only thing
  //   on the card stating what kind of page it is.
  internalTag: { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.3, color: V300, border: `1px solid ${V500}4D`, background: `${V500}1F`, padding: '4px 10px', borderRadius: 999 },
  checking: { padding: '28px 0', textAlign: 'center', color: MUTED, fontSize: 14 },
  title: { fontFamily: DISPLAY, fontSize: 25, fontWeight: 700, margin: '0 0 8px', color: TEXT, letterSpacing: '-0.02em' },
  sub: { fontSize: 13.5, color: MUTED, margin: '0 0 22px', lineHeight: 1.5 },
  label: { display: 'block', fontSize: 12, color: MUTED, margin: '14px 0 6px', fontWeight: 600 },
  // ⚠ WCAG 1.4.11 asks 3.0 of a control's boundary; the app's border tones are
  //   ~1.3 here and that boundary is the only thing showing where the field is.
  input: { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 9, border: `1px solid ${MUTED}`, background: ELEVATED, color: TEXT, fontSize: 14, outline: 'none', fontFamily: UI },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  option: { color: '#111827', background: '#FFFFFF' },
  hint: { fontSize: 11.5, color: MUTED, marginTop: 6 },
  checkRow: { display: 'flex', gap: 10, alignItems: 'flex-start', margin: '16px 0 2px', fontSize: 12.5, lineHeight: 1.45, color: MUTED, cursor: 'pointer' },
  btn: { width: '100%', marginTop: 22, padding: '13px 18px', borderRadius: 9, border: 'none', background: `linear-gradient(120deg, ${V600}, ${V500})`, color: '#FFFFFF', fontWeight: 700, fontSize: 14.5, cursor: 'pointer', fontFamily: UI },
  row: { display: 'flex', justifyContent: 'space-between', marginTop: 16 },
  linkBtn: { background: 'none', border: 'none', color: V300, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontFamily: UI },
  error: { marginTop: 14, fontSize: 13, color: ERROR, background: `${ERROR}1A`, border: `1px solid ${ERROR}4D`, borderRadius: 8, padding: '9px 12px' },
  foot: { marginTop: 26, fontSize: 11, color: MUTED, textAlign: 'center' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.6, color: MUTED, borderBottom: `1px solid ${EDGE}` },
  td: { padding: '12px', borderBottom: `1px solid ${EDGE}`, verticalAlign: 'top' },
  tdSub: { fontSize: 12, color: MUTED },
  approveBtn: { padding: '7px 13px', marginLeft: 6, borderRadius: 8, border: 'none', cursor: 'pointer', background: V600, color: '#FFFFFF', fontWeight: 700, fontSize: 12.5, fontFamily: UI },
  rejectBtn: { padding: '7px 13px', marginLeft: 6, borderRadius: 8, cursor: 'pointer', background: 'transparent', border: `1px solid ${ERROR}66`, color: ERROR, fontWeight: 600, fontSize: 12.5, fontFamily: UI },
}
